// Shared copy for /audit and /front-office — used by the React pages AND scripts/prerender.mjs.
const SITE_URL = "https://gadgetguymedia.com";

const AREA_SERVED = [
  { "@type": "City", name: "Gallatin, TN" },
  { "@type": "City", name: "Hendersonville, TN" },
  { "@type": "AdministrativeArea", name: "Sumner County, TN" },
  { "@type": "Place", name: "Middle Tennessee" },
];

const PROVIDER = {
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "Gadget Guy Media",
  url: SITE_URL,
  address: { "@type": "PostalAddress", addressLocality: "Gallatin", addressRegion: "TN", addressCountry: "US" },
};

const faqSchema = (path, faqs) => ({
  "@type": "FAQPage",
  "@id": `${SITE_URL}${path}#faq`,
  url: `${SITE_URL}${path}`,
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const FIX_PRICES = [
  { name: "Free Local AI Audit", price: "$0", use: "Find the gaps" },
  { name: "Build Blueprint", price: "$297, credited to your build", use: "A detailed plan and fixed quote for the fixes you pick" },
  { name: "AI Front Desk", price: "$1,500", use: "A website chatbot that answers common questions 24/7 and collects requests for your staff", to: "/front-office" },
  { name: "Automation Sprint", price: "$2,000", use: "One repetitive workflow, automated end to end" },
  { name: "Custom AI Tool", price: "from $3,500", use: "A tool built around how your business works" },
  { name: "Care plan", price: "$199–$499/mo", use: "Optional upkeep after launch" },
];

export const audit = {
  path: "/audit",
  title: "Free Local AI Audit for Small Business | Gallatin, TN",
  description:
    "Free Local AI Audit for Sumner County businesses: I check your website, Google listing and AI answers, then send a plain-English fix list. No pitch.",
  ogImage: `${SITE_URL}/brand-v2/favicon/og-audit.png`,
  h1: "Free Local AI Audit: find out where your business is losing customers online",
  lede:
    "I'll check your website, your Google listing, and how you show up when people ask AI tools like ChatGPT for a business like yours. Then I'll send you a plain-English report with the fixes ranked by what brings in the most customers.",
  badges: ["Free", "Real person, not a quiz", "Report in 3 business days"],
  covers: [
    { t: "Your website", d: "How fast it loads on a phone, how easy it is to call, book or ask a question, whether your forms work on a phone, and whether anything is broken." },
    { t: "Your Google listing and directories", d: "Do your hours, address and phone number match on your website, Google and the other listings people find? When they don't, customers show up when you're closed, and they stop trusting any of them." },
    { t: "How you show up in AI answers", d: "More people are asking ChatGPT, Google's AI answers and similar tools \"who's a good ___ near me?\" I check whether those tools can read your site, what they say about you, and who they recommend instead." },
  ],
  gets: [
    ["A scorecard", "with a grade and a one-line finding for each area"],
    ["What's already working,", "so you don't break it"],
    ["Where you're losing customers,", "with the evidence"],
    ["A ranked fix list:", "what to do first, what you can do yourself, and what it costs if you want me to do it"],
    ["What not to spend money on.", "If AI isn't the answer, I'll say so."],
  ],
  caseIntro:
    "I built fhcog.net for Family Health Care of Gallatin. Then I ran this exact audit on it, and I still found real problems. That's the point: even a newer site has gaps you can't see from the inside.",
  working: [
    "AI tools are explicitly welcome. The site has an AI-friendly robots.txt, an llms.txt file, and FAQ markup that AI assistants can read.",
    "It came up first for the clinic's own name in my search checks.",
    "Phone, address and hours are easy to find, with tap-to-call on phones.",
  ],
  found: [
    ["Booking is phone-only.", "Once the office closes, an evening visitor can leave a voicemail or send a form, and that's it."],
    ["Slow on phones.", "In a Lighthouse lab test, the site scored 46 out of 100 on mobile vs 82 on desktop."],
    ["Listings don't agree.", "Some listings show different hours than the website (one skips the lunch break), and the address shows up as both \"831\" and \"831B\" Nashville Pike."],
    ["The new-patient forms are a scanned PDF.", "Patients can't fill them out on a phone. They have to print them."],
    ["No separate service pages.", "The clinic offers DOT physicals, but there's no page for it, so it didn't show up when I searched for a DOT physical in Gallatin."],
  ],
  // Hidden "after" slot. Set to e.g. { date: "Nov 2026", text: "Re-test: mobile score 46 → 91, listings match." } once fixes are live.
  caseAfter: {
    date: "Oct 21, 2026",
    text: "Re-test: audit score 66 → 85, desktop speed 82 → 97, page about 61% lighter, plus a 24/7 AI front desk and six AI-readable service pages.",
    to: "/case-studies/family-health-care-gallatin",
  },
  rankedFixes:
    "an after-hours AI Front Desk, faster mobile pages, matching listings, a page for each key service, and fillable online forms.",
  steps: [
    ["Request your audit.", "Fill out the short form below with your website and what you'd like more of: calls, bookings, quotes."],
    ["I do the digging.", "I review your site, your listings, and your Google and AI search results. You don't have to do anything."],
    ["You get your report", "within 3 business days."],
    ["You decide.", "Do the fixes yourself, hand them to anyone, or have me build them. Want to talk it through? We can do a quick 15-minute walkthrough call by Zoom, phone, or in person in Sumner County."],
  ],
  whoIntro:
    "Owners of local businesses around Gallatin, Hendersonville and Sumner County: trades, clinics and practices, shops, restaurants, professional services. It's a good fit if:",
  whoFit: [
    "Customers find you online but mostly call to book",
    "Your phone goes to voicemail after hours",
    "You're not sure what Google or ChatGPT says about you",
    "Someone has quoted you a monthly AI subscription and you want a second opinion",
  ],
  whoRemote: "Outside Middle Tennessee? I do the audit remotely too. Just mention it in the form.",
  why:
    "I'm Robert Warren, and Gadget Guy Media is my one-person AI studio in Gallatin, TN. You won't get a sales team or a call center. The person who reviews your business is the same person who would build the fix, and you own what I build.",
  faqs: [
    { q: "Is the Local AI Audit really free?", a: "Yes. No card, no contract, no catch. You keep the report whether you hire me or not." },
    { q: "What does the free AI audit look at?", a: "Three things: your website (speed on a phone, how people book or contact you, forms, broken links), your Google listing and other directory listings (do your hours, address and phone match everywhere?), and how your business shows up when people ask AI tools like ChatGPT for a business like yours." },
    { q: "What do I get at the end?", a: "A short, branded report in plain English: a scorecard, what's already working, where you're losing customers, and a ranked list of fixes. Each fix says whether you can do it yourself or what it would cost to have me do it." },
    { q: "How long does the audit take?", a: "You fill out a short form. I do the review and send your report within 3 business days. If you want, we can walk through it together on a quick call." },
    { q: "Is this just a sales pitch?", a: "No. The fixes are yours to do yourself, hand to your web person, or have me build. If something isn't worth spending money on, the report says so." },
    { q: "Do I need to know anything about AI?", a: "No. Send me your website and tell me what's eating your time. I'll handle the technical part and explain it in plain English." },
    { q: "What does it cost if I want you to fix what the audit finds?", a: "Prices are fixed and published. A Build Blueprint is $297 and is credited in full to your build. The AI Front Desk website chatbot is $1,500, an Automation Sprint is $2,000, and a Custom AI Tool starts at $3,500. An optional care plan runs $199 to $499 a month." },
  ],
  replyNote: "No sales team. You'll hear from Robert directly, within 1 business day.",
};

audit.schema = {
  "@context": "https://schema.org",
  "@graph": [
    faqSchema(audit.path, audit.faqs),
    {
      "@type": "Service",
      "@id": `${SITE_URL}/audit#service`,
      name: "Free Local AI Audit",
      serviceType: "Small business website, local listing and AI search visibility audit",
      description:
        "A free, human-run review of a small business's website, Google listing and directory listings, and visibility in AI answers like ChatGPT, delivered as a plain-English report with a ranked list of fixes.",
      url: `${SITE_URL}/audit`,
      provider: PROVIDER,
      areaServed: AREA_SERVED,
      offers: [
        { "@type": "Offer", name: "Free Local AI Audit", price: "0", priceCurrency: "USD", url: `${SITE_URL}/audit` },
        { "@type": "Offer", name: "Build Blueprint", price: "297", priceCurrency: "USD", description: "Detailed build plan and fixed quote, credited in full to the build." },
      ],
    },
  ],
};

export const frontOffice = {
  path: "/front-office",
  title: "AI Front Desk Website Chatbot, $1,500 | Gadget Guy Media",
  description:
    "A website chatbot trained on your business. It answers common questions 24/7 and collects requests for your staff. $1,500 fixed. Built in Gallatin, TN.",
  ogImage: `${SITE_URL}/brand-v2/favicon/og-front-office.png`,
  h1: "The AI Front Desk: a website chatbot that answers when you can't",
  lede:
    "A chatbot for your website, trained on your business. It answers the questions your staff hears every day, around the clock, and collects requests so your team can follow up in the morning. $1,500 fixed price, and you own it.",
  badges: ["$1,500 one time", "Trained on your info, not the whole internet", "Hands off to a real person"],
  whatIs:
    "The AI Front Desk from Gadget Guy Media is a custom chatbot for your website. It answers visitors' common questions from your own business information, 24/7, and collects their name, contact info and request so your staff can follow up and confirm. It's a $1,500 fixed-price build, and you own it.",
  whatIsNote: "It's a website chatbot, not a phone answering service. It lives on your website as a chat window. It doesn't answer calls or texts.",
  does: [
    { t: "Answers the everyday questions", d: "\"Are you open Saturday?\" \"Do you take my insurance?\" \"Do you serve Hendersonville?\" \"How much is a ___?\" It answers from the hours, services, prices and FAQs you approve." },
    { t: "Collects the request", d: "When someone wants an appointment, a quote or a callback, it takes their name, phone or email, and what they need, then emails it straight to your team." },
    { t: "Knows when to hand off", d: "If it doesn't know the answer, it says so, and it doesn't guess. It collects the visitor's contact info so a real person can follow up." },
    { t: "Stays on your rules", d: "You decide what it should and shouldn't talk about. A medical office, for example, can have it skip symptoms and point people to the patient portal or 911." },
  ],
  whoIntro:
    "Local businesses that get the same questions over and over and lose people after hours: clinics and practices, home services and trades, salons, shops, law and insurance offices. It's a good fit if:",
  whoFit: [
    "Most of your bookings start with a phone call",
    "Your voicemail fills up overnight",
    "Your staff answers the same 10 questions every day",
  ],
  exampleIntro:
    "I built fhcog.net for Family Health Care of Gallatin, and when I audited it, the biggest gap was simple: booking is phone-only. Once the office closes, an evening visitor can leave a voicemail or send a form, and that's it.",
  exampleDoes: [
    "Answer \"Are you taking new patients?\", \"Do you take my insurance?\", \"Can I walk in?\", and \"Where are the forms?\" any time of day",
    "Take a name and callback number so the front desk starts the morning with a list of people to call back",
    "Stay away from symptoms and medical details, and point medical questions to the portal or 911",
  ],
  exampleNote: "This is the fix the audit recommended. It hasn't been built yet.",
  included: [
    "Setup call to gather your hours, services, prices, policies and FAQs",
    "A chatbot trained on that information, styled to match your website",
    "Request and lead collection, sent to your team by email",
    "Handoff rules: what it answers, what it passes to a person",
    "A test link so you can try it before it goes live",
    "Launch on your website plus a walkthrough",
    "2 weeks of post-launch fixes and tweaks",
    "Basic app hosting, free for the first year, then $75 a month after that. Includes basic maintenance.",
  ],
  extras: "Calendar booking, phone or text, extra languages, and connecting to other software are add-ons or a separate project.",
  after: [
    ["Hosting: free for the first year, then $75/mo.", "Basic app hosting and basic maintenance are included free for the first 12 months. After that it's $75 a month. You still own the chatbot."],
    ["AI usage:", "the AI service is billed by the provider at cost, with no markup, based on how much your chatbot gets used. For most small businesses that's a few dollars to a few tens of dollars a month."],
    ["Optional care plan, $199–$499/mo:", "I keep the answers current when your hours, prices or services change, and tune it based on what people actually ask. The price depends on how often things change."],
  ],
  compare: [
    ["Upfront", "$1,500", "Often low or none"],
    ["Monthly fee", "$0 the first year, then $75/mo hosting (AI usage at cost)", "Every month"],
    ["Who owns it", "You", "The provider"],
    ["If you stop paying", "You keep it", "It turns off"],
    ["Who you talk to", "Robert, the person who built it", "Support line"],
  ],
  compareNote: "If what you really need is someone answering your phone, a phone-answering service may fit better, and I'll tell you so.",
  steps: [
    ["Free Local AI Audit.", "I check whether a chatbot is the right fix for your business, or whether something cheaper comes first.", "/audit"],
    ["Gather your info.", "Hours, services, prices, policies and the questions you get the most."],
    ["Build and test.", "You try it on a private link and tell me what to change."],
    ["Go live.", "It goes on your website, and you get a walkthrough. Most builds go live in about two weeks."],
  ],
  blueprint:
    "Bigger project? A Build Blueprint ($297, credited to your build) maps out the scope and gives you a fixed quote first. The AI Front Desk is already fixed-scope, so you don't need a Blueprint for it.",
  local:
    "I'm Robert Warren, and Gadget Guy Media is my one-person AI studio in Gallatin. I build for businesses in Gallatin, Hendersonville, Sumner County and across Middle Tennessee, and for remote clients too. No sales team, and no handoffs to a support line. You work directly with the person who builds your chatbot.",
  faqs: [
    { q: "What is the AI Front Desk?", a: "The AI Front Desk from Gadget Guy Media is a custom chatbot for your website. It answers visitors' common questions from your own business information, around the clock, and collects their name, contact info and request so your staff can follow up and confirm. It's a $1,500 fixed-price build." },
    { q: "How much does it cost?", a: "$1,500, one time. Basic app hosting with basic maintenance is free for the first year, then $75 a month after that. The AI service is billed by the provider at cost, with no markup, based on how much the chatbot gets used. An optional care plan from $199 to $499 a month covers updates and tuning." },
    { q: "Does it answer phone calls or texts?", a: "No. The AI Front Desk is a chatbot on your website. Phone calls and text messages are not part of the $1,500 build." },
    { q: "Will it make things up?", a: "It's set up to answer only from the information you approve, like your hours, services, prices and FAQs. If it doesn't know, it says so and collects the visitor's contact info so a real person can follow up." },
    { q: "Can it book appointments?", a: "It collects appointment and quote requests, and your staff confirms them. Direct calendar booking is available as an add-on." },
    { q: "How long does it take to go live?", a: "Most builds go live in about two weeks after we agree on the scope, and you get to test it before it goes live." },
    { q: "Do I own it?", a: "Yes. Once it's paid for, the chatbot, its setup and its content are yours. Hosting is free for the first year, then $75 a month for basic hosting and maintenance, plus the optional care plan if you choose it." },
  ],
};

frontOffice.schema = {
  "@context": "https://schema.org",
  "@graph": [
    faqSchema(frontOffice.path, frontOffice.faqs),
    {
      "@type": "Service",
      "@id": `${SITE_URL}/front-office#service`,
      name: "AI Front Desk (website chatbot)",
      alternateName: "AI Front Desk from Gadget Guy Media",
      serviceType: "AI chatbot development",
      description:
        "A custom website chatbot trained on a small business's own information. It answers common visitor questions 24/7 and collects requests and leads for staff to follow up and confirm. One-time fixed price; the business owns the build.",
      url: `${SITE_URL}/front-office`,
      provider: PROVIDER,
      areaServed: AREA_SERVED,
      offers: [
        { "@type": "Offer", name: "AI Front Desk build", price: "1500", priceCurrency: "USD", url: `${SITE_URL}/front-office`, description: "One-time fixed price. First year of basic hosting and maintenance included; AI service usage billed at cost." },
        {
          "@type": "Offer",
          name: "Basic hosting and maintenance (after year one)",
          priceCurrency: "USD",
          priceSpecification: { "@type": "UnitPriceSpecification", price: "75", priceCurrency: "USD", unitText: "MONTH" },
        },
        {
          "@type": "Offer",
          name: "Care plan (optional)",
          priceCurrency: "USD",
          priceSpecification: { "@type": "UnitPriceSpecification", minPrice: "199", maxPrice: "499", priceCurrency: "USD", unitText: "MONTH" },
        },
      ],
    },
  ],
};
